<script setup lang="ts">
definePageMeta({
  name: 'exchange-fund-security',
})

import {
  getTraderAssetReserveListPage,
  getTraderFundFlowList,
  getTraderTradingVolumeListPage,
  type TraderAssetReserveListResponse,
  type TraderTradingVolumeListResponse,
} from '~/api/trader-funds-detail'
import { getTraderDetail, type TraderDetailAssetStrength } from '~/api/trader-detail'
import { getAliasResolve, getTraderAssetStrengthChart } from '~/server/detail'

type DetailTabKey = 'reserve' | 'flow' | 'volume'
type VolumeDetailCategoryKey = 0 | 1 | 2

type VolumeTableVariant = 'spot' | 'perpetual' | 'futures'

const route = useRoute()
const { locale, t } = useAppI18n()
const DETAILS_SCROLL_OFFSET = 96
const DEFAULT_TIME_RANGE_TYPE = 5

/** 清洗路由参数和接口字段，避免 null/undefined 直接参与展示。 */
const normalizeRouteText = (value: unknown) => (value === null || value === undefined ? '' : String(value).trim())

/** 规范化图表时间范围参数，兜底为默认周期。 */
const normalizeTimeRangeType = (timeRangeType: string | number) => {
  const normalizedType = Number(timeRangeType)

  return Number.isFinite(normalizedType) ? normalizedType : DEFAULT_TIME_RANGE_TYPE
}

/** 将详情接口的时间范围字段统一为资金卡片可消费的 label/value 结构。 */
const normalizeAssetStrengthTimeRanges = (timeRange: unknown) => {
  if (Array.isArray(timeRange)) {
    return timeRange
      .map((item) => {
        const option = item as { label?: unknown; value?: unknown }
        const value = normalizeRouteText(option.value)

        return value
          ? {
              label: normalizeRouteText(option.label) || value,
              value,
            }
          : null
      })
      .filter((option): option is { label: string; value: string } => option !== null)
  }

  const options = (timeRange as { options?: unknown } | null)?.options

  if (!Array.isArray(options)) {
    return []
  }

  return options.map((option) => {
    const value = normalizeRouteText(option)

    return {
      label: value,
      value,
    }
  })
}

const getChartPointCount = (chart?: { points?: unknown[]; pointsVal?: unknown[] }) =>
  Math.max(chart?.points?.length ?? 0, chart?.pointsVal?.length ?? 0)

/** 统一提取路由参数首项，兼容 string 和 string[]。 */
const getFirstRouteValue = (value: unknown) => {
  if (Array.isArray(value)) {
    return normalizeRouteText(value[0])
  }

  return normalizeRouteText(value)
}

/** 当前路由文件是 `[slug]/fund-security.vue`，这里直接从 slug 动态段提取交易所标识。 */
const exchangeSlug = computed(() => getFirstRouteValue(route.params.slug))

const { data: aliasResolveData } = await useAsyncData(
  () => `alias-resolve-${exchangeSlug.value}`,
  () => getAliasResolve({ alias: exchangeSlug.value }),
)

const traderCode = computed(() => {
  const resolvedData = aliasResolveData.value?.data as Record<string, any>
  const items = resolvedData?.items
  const resolvedCode = items?.find((item: any) => item.platform === 2)?.traderCode

  return resolvedCode
})
const exchangeId = computed(() => traderCode.value || exchangeSlug.value)

const exchangeName = computed(() => {
  const slug = exchangeSlug.value || traderCode.value

  return slug
    .split('-')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
})

/** 基础信息负责面包屑、资产实力卡片和部分 header 兜底文案。 */
const { data: traderDetailData, pending } = await useAsyncData(
  () => `trader-funds-detail-${traderCode.value}-${locale}`,
  async () => {
    return await getTraderDetail({
      traderCode: traderCode.value,
    })
  },
  {
    watch: [traderCode],
  },
)

const breadcrumbExchangeName = computed(
  () =>
    normalizeRouteText(traderDetailData.value?.basicInfo?.showName) ||
    normalizeRouteText(traderDetailData.value?.traderData?.basicData?.showName) ||
    exchangeName.value ||
    traderCode.value,
)
const exchangeLogo = computed(() => normalizeRouteText(traderDetailData.value?.basicInfo?.ico))
const fundsExchangeName = computed(() => {
  return normalizeRouteText(traderDetailData.value?.basicInfo?.shortName) || breadcrumbExchangeName.value
})

const fundSecuritySeoTitle = computed(() => {
  const title = t('84381', [breadcrumbExchangeName.value])
  return title.includes('WikiBit') ? title : `${title} | WikiBit`
})

useHead(() => ({
  title: fundSecuritySeoTitle.value,
  meta: [
    {
      name: 'description',
      content: t('84398', [breadcrumbExchangeName.value, breadcrumbExchangeName.value]),
    },
  ],
}))

/** 当前页面包屑：首页回首页，交易所名称回当前 slug 对应的 review 页面。 */
const breadcrumbItems = computed(() => {
  return [
    {
      label: t('82520'),
      route: {
        name: 'home',
      },
    },
    {
      label: breadcrumbExchangeName.value,
      route: {
        name: 'exchange-detail',
        params: {
          slug: exchangeSlug.value,
        },
      },
    },
    {
      label: t('84441'),
    },
  ]
})

/** 页面一级 tab 文案跟随语言包切换。 */
const detailTabs = computed<Array<{ key: DetailTabKey; label: string }>>(() => [
  { key: 'reserve', label: t('82721') },
  { key: 'flow', label: t('82722') },
  { key: 'volume', label: t('82723') },
])

const RESERVE_PAGE_SIZE = 50
const FUND_FLOW_PAGE_SIZE = 50
const TRADING_VOLUME_PAGE_SIZE = 50
const activeDetailTab = ref<DetailTabKey>('reserve')
const activeVolumeCategory = ref<VolumeDetailCategoryKey>(0)
const selectedFundFlowTimeRangeType = ref('5')
const selectedTradingVolumeTimeRangeType = ref('5')
const hasSelectedFundFlowPeriod = ref(false)
const hasSelectedTradingVolumePeriod = ref(false)
const currentPage = ref(1)
const isDetailLoading = ref(false)
const copiedRowId = ref('')
const detailsSectionRef = ref<HTMLElement | null>(null)
let loadingTimer: ReturnType<typeof window.setTimeout> | null = null
let copiedTimer: ReturnType<typeof window.setTimeout> | null = null

/** 资金流向时间 Tab 直接使用交易所详情接口返回的 assetStrength.fundFlowTimeRange。 */
const fundFlowTimeRangeOptions = computed(() =>
  normalizeAssetStrengthTimeRanges(traderDetailData.value?.assetStrength?.fundFlowTimeRange),
)
/** 交易量时间 Tab 使用详情接口的 assetStrength.tradingVolumeTimeRange。 */
const tradingVolumeTimeRangeOptions = computed(() =>
  normalizeAssetStrengthTimeRanges(traderDetailData.value?.assetStrength?.tradingVolumeTimeRange),
)

/** 下方交易量明细接口使用时间范围在选项数组中的索引值。 */
const tradingVolumeListTimeRangeType = computed(() => {
  const options = tradingVolumeTimeRangeOptions.value
  const selectedIndex = options.findIndex((option) => option.value === selectedTradingVolumeTimeRangeType.value)

  return Math.max(selectedIndex, 0)
})

/** 首屏使用详情接口图表；用户切换资金流时间后再增量加载图表接口。 */
const { data: traderAssetStrengthChart } = useAsyncData(
  () => `trader-asset-strength-chart-flow-${traderCode.value}-${selectedFundFlowTimeRangeType.value}-${locale}`,
  async () => {
    if (!traderCode.value || !hasSelectedFundFlowPeriod.value) {
      return null
    }

    return await getTraderAssetStrengthChart({
      traderCode: traderCode.value,
      timeRangeType: normalizeTimeRangeType(selectedFundFlowTimeRangeType.value),
    })
  },
  {
    default: () => null,
    immediate: false,
    lazy: true,
    server: false,
    watch: [traderCode, selectedFundFlowTimeRangeType, hasSelectedFundFlowPeriod],
  },
)

/** 首屏使用详情接口交易量图表，避免列表数据覆盖时间轴与数值。 */
const { data: traderTradingVolumeAssetStrengthChart } = useAsyncData(
  () => `trader-asset-strength-chart-volume-${traderCode.value}-${selectedTradingVolumeTimeRangeType.value}-${locale}`,
  async () => {
    if (!traderCode.value || !hasSelectedTradingVolumePeriod.value) {
      return null
    }

    return await getTraderAssetStrengthChart({
      traderCode: traderCode.value,
      timeRangeType: normalizeTimeRangeType(selectedTradingVolumeTimeRangeType.value),
    })
  },
  {
    default: () => null,
    immediate: false,
    lazy: true,
    server: false,
    watch: [traderCode, selectedTradingVolumeTimeRangeType, hasSelectedTradingVolumePeriod],
  },
)

watch(
  fundFlowTimeRangeOptions,
  (options) => {
    if (options.length === 0) {
      return
    }

    const hasSelectedFlowOption = options.some((option) => option.value === selectedFundFlowTimeRangeType.value)

    if (!hasSelectedFlowOption) {
      selectedFundFlowTimeRangeType.value = options[0].value
    }
  },
  { immediate: true },
)

watch(
  tradingVolumeTimeRangeOptions,
  (options) => {
    if (options.length === 0) {
      return
    }

    const hasSelectedOption = options.some((option) => option.value === selectedTradingVolumeTimeRangeType.value)

    if (!hasSelectedOption) {
      selectedTradingVolumeTimeRangeType.value = options[0].value
    }
  },
  { immediate: true },
)

/** 顶部资产实力卡片完全复用 review 页的数据拼装逻辑，确保展示一致。 */
const exchangeFunds = computed<TraderDetailAssetStrength | null>(() => {
  const assetStrength = traderDetailData.value?.assetStrength

  if (!assetStrength) {
    return null
  }

  const fundFlowTimeRanges = fundFlowTimeRangeOptions.value
  const tradingVolumeTimeRanges = tradingVolumeTimeRangeOptions.value
  const assetStrengthChart = traderAssetStrengthChart.value
  const tradingVolumeAssetStrengthChart = traderTradingVolumeAssetStrengthChart.value
  const chartFundFlow = assetStrengthChart?.fundFlow
  const chartTradingVolume = tradingVolumeAssetStrengthChart?.tradingVolume
  const originalFundFlow = assetStrength.fundFlow ?? {}
  const originalTradingVolume = assetStrength.tradingVolume ?? {}
  const originalFundFlowChart = originalFundFlow.chart
  const chartFundFlowChart = chartFundFlow?.chart
  const fundFlowChart =
    hasSelectedFundFlowPeriod.value && getChartPointCount(chartFundFlowChart) > 0
      ? chartFundFlowChart
      : getChartPointCount(originalFundFlowChart) >= getChartPointCount(chartFundFlowChart)
        ? originalFundFlowChart
        : chartFundFlowChart
  const tradingVolumeChart = chartTradingVolume?.chart
  const updatedTags = Array.isArray(assetStrength.tags)
    ? assetStrength.tags.map((tag: { label?: string; value?: string }) => {
        const tagLabel = normalizeRouteText(tag.label)

        if (!tagLabel.includes(t('82659'))) {
          return tag
        }

        return {
          ...tag,
          value: normalizeRouteText(chartFundFlow?.badge) || tag.value,
        }
      })
    : assetStrength.tags

  return {
    ...assetStrength,
    tags: updatedTags,
    timeRange: fundFlowTimeRanges.length > 0 ? fundFlowTimeRanges : assetStrength.timeRange,
    fundFlowTimeRange: fundFlowTimeRanges.length > 0 ? fundFlowTimeRanges : assetStrength.fundFlowTimeRange,
    tradingVolumeTimeRange:
      tradingVolumeTimeRanges.length > 0 ? tradingVolumeTimeRanges : assetStrength.tradingVolumeTimeRange,
    fundFlow: {
      ...originalFundFlow,
      ...chartFundFlow,
      amount: normalizeRouteText(chartFundFlow?.amount) || originalFundFlow.amount,
      changePercent: normalizeRouteText(chartFundFlow?.changePercent) || originalFundFlow.changePercent,
      changeDirection: normalizeRouteText(chartFundFlow?.changeDirection) || originalFundFlow.changeDirection,
      badge: normalizeRouteText(chartFundFlow?.badge) || originalFundFlow.badge,
      chart: fundFlowChart || originalFundFlow.chart,
    },
    tradingVolume: {
      ...originalTradingVolume,
      ...chartTradingVolume,
      chart: tradingVolumeChart || originalTradingVolume.chart,
    },
  }
})

/** 资产储备明细是默认 tab，需要首屏直接请求。 */
const { data: assetReserveListData, pending: assetReserveListPending } =
  await useAsyncData<TraderAssetReserveListResponse>(
    () => `trader-asset-reserve-list-${exchangeId.value}-${currentPage.value}-${RESERVE_PAGE_SIZE}-${locale}`,
    async () => {
      return await getTraderAssetReserveListPage({
        exchangeId: exchangeId.value,
        page: currentPage.value,
        size: RESERVE_PAGE_SIZE,
      })
    },
    {
      watch: [exchangeId, currentPage],
    },
  )

/** 资金流向和交易量只在对应 tab 激活时请求，避免首屏并发过重。 */
const { data: fundFlowListData, pending: fundFlowListPending } = await useAsyncData(
  () =>
    `trader-fund-flow-list-${exchangeId.value}-${selectedFundFlowTimeRangeType.value}-${currentPage.value}-${FUND_FLOW_PAGE_SIZE}-${locale}`,
  async () => {
    if (activeDetailTab.value !== 'flow') {
      return null
    }

    return await getTraderFundFlowList({
      exchangeId: exchangeId.value,
      timeRangeType: normalizeTimeRangeType(selectedFundFlowTimeRangeType.value),
      page: currentPage.value,
      size: FUND_FLOW_PAGE_SIZE,
    })
  },
  {
    watch: [exchangeId, selectedFundFlowTimeRangeType, currentPage, activeDetailTab],
  },
)

const { data: tradingVolumeListData, pending: tradingVolumeListPending } = await useAsyncData(
  () =>
    `trader-trading-volume-list-${exchangeId.value}-${tradingVolumeListTimeRangeType.value}-${activeVolumeCategory.value}-${currentPage.value}-${TRADING_VOLUME_PAGE_SIZE}-${locale}`,
  async () => {
    if (activeDetailTab.value !== 'volume') {
      return {
        data: [],
        total: 0,
      } as TraderTradingVolumeListResponse
    }

    return await getTraderTradingVolumeListPage({
      exchangeId: exchangeId.value,
      timeRangeType: tradingVolumeListTimeRangeType.value,
      subTabType: activeVolumeCategory.value,
      page: currentPage.value,
      size: TRADING_VOLUME_PAGE_SIZE,
    })
  },
  {
    watch: [exchangeId, tradingVolumeListTimeRangeType, activeDetailTab, activeVolumeCategory, currentPage],
  },
)

const reserveTotal = computed(() => Math.max(Number(assetReserveListData.value?.total || 0), 0))
const fundFlowTotal = computed(() => Math.max(Number(fundFlowListData.value?.total || 0), 0))
/** 交易量明细子 tab 文案。 */
const tradingVolumeCategoryTabs = computed<Array<{ key: VolumeDetailCategoryKey; label: string }>>(() => [
  { key: 0, label: t('84442') },
  { key: 1, label: t('84443') },
  { key: 2, label: t('84444') },
])

/** 根据交易量子 tab 映射出当前表格结构。 */
const activeVolumeTableVariant = computed<VolumeTableVariant>(() => {
  if (activeVolumeCategory.value === 1) {
    return 'perpetual'
  }

  if (activeVolumeCategory.value === 2) {
    return 'futures'
  }

  return 'spot'
})

const tradingVolumeTotal = computed(() => Math.max(Number(tradingVolumeListData.value?.total || 0), 0))

/** 当前空状态标题复用激活 tab 文案。 */
const activeDetailTitle = computed(() => {
  return detailTabs.value.find((tab) => tab.key === activeDetailTab.value)?.label || t('82723')
})
/** 当前激活明细 tab 对应的总条数，由接口 total 驱动统一分页组件。 */
const activeDetailTotal = computed(() => {
  if (activeDetailTab.value === 'flow') {
    return fundFlowTotal.value
  }

  if (activeDetailTab.value === 'volume') {
    return tradingVolumeTotal.value
  }

  return reserveTotal.value
})

/** 根据当前 tab 选择对应列表的分页大小。 */
const activePageSize = computed(() => {
  if (activeDetailTab.value === 'flow') {
    return FUND_FLOW_PAGE_SIZE
  }

  if (activeDetailTab.value === 'volume') {
    return TRADING_VOLUME_PAGE_SIZE
  }

  return RESERVE_PAGE_SIZE
})
const shouldShowPagination = computed(() => activeDetailTotal.value > activePageSize.value)
const totalPages = computed(() => Math.max(1, Math.ceil(activeDetailTotal.value / activePageSize.value)))
/** 仅用于给公共分页组件渲染结构，本页实际点击后仍拦截为本地翻页。 */
const fundsPageRoute = computed(() => `/exchanges/${exchangeSlug.value}/fund-security/page/{idx}`)
const activeDetailPending = computed(() => {
  if (activeDetailTab.value === 'flow') {
    return fundFlowListPending.value
  }

  if (activeDetailTab.value === 'volume') {
    return tradingVolumeListPending.value
  }

  return assetReserveListPending.value
})
const isActiveDetailLoading = computed(() => isDetailLoading.value || activeDetailPending.value)

/** 切换 tab / 分页时统一滚动到明细区域，并短暂展示骨架过渡。 */
const runDetailsLoading = async () => {
  isDetailLoading.value = true

  if (loadingTimer) {
    window.clearTimeout(loadingTimer)
    if (detailsSectionRef.value) {
      detailsSectionRef.value.style.minHeight = ''
    }
  }

  // 记录并锁死当前区域高度，防止内容收缩/骨架屏加载导致滚动条回弹或定位失效
  const currentHeight = detailsSectionRef.value?.offsetHeight
  if (currentHeight && detailsSectionRef.value) {
    detailsSectionRef.value.style.minHeight = `${currentHeight}px`
  }

  await nextTick()
  if (detailsSectionRef.value && typeof window !== 'undefined') {
    window.scrollTo({
      top: Math.max(detailsSectionRef.value.getBoundingClientRect().top + window.scrollY - DETAILS_SCROLL_OFFSET, 0),
      behavior: 'smooth',
    })
  }

  loadingTimer = window.setTimeout(() => {
    isDetailLoading.value = false
    loadingTimer = null
    // 恢复原有高度控制
    if (detailsSectionRef.value) {
      detailsSectionRef.value.style.minHeight = ''
    }
  }, 420)
}

const handleFundFlowPeriodChange = (value: string) => {
  hasSelectedFundFlowPeriod.value = true
  selectedFundFlowTimeRangeType.value = value
  currentPage.value = 1
  if (activeDetailTab.value === 'flow') {
    void runDetailsLoading()
  }
}

const handleTradingVolumePeriodChange = (value: string) => {
  hasSelectedTradingVolumePeriod.value = true
  selectedTradingVolumeTimeRangeType.value = value
  currentPage.value = 1
  if (activeDetailTab.value === 'volume') {
    void runDetailsLoading()
  }
}

/** 三类明细共用一套分页状态，切换 tab 或子分类时都回到第一页。 */
const selectDetailTab = async (tab: DetailTabKey) => {
  if (activeDetailTab.value === tab) {
    return
  }

  activeDetailTab.value = tab
  if (tab !== 'volume') {
    activeVolumeCategory.value = 0
  }
  currentPage.value = 1
  await runDetailsLoading()
}

/** 切换交易量子 tab 时同步重置分页并滚动到明细区。 */
const selectVolumeCategory = async (category: VolumeDetailCategoryKey) => {
  if (activeVolumeCategory.value === category) {
    return
  }

  activeVolumeCategory.value = category
  currentPage.value = 1
  await runDetailsLoading()
}

/** 切换分页前校验页码范围，并保持 tab 区域可见。 */
const selectPage = async (page: number) => {
  const nextPage = Math.max(page, 1)

  if (nextPage === currentPage.value || nextPage > totalPages.value) {
    return
  }

  currentPage.value = nextPage
  await runDetailsLoading()
}

/** 拦截公共分页组件的链接点击，继续复用当前页面已有的本地翻页逻辑。 */
const handlePaginationClick = async (event: MouseEvent) => {
  const anchor = (event.target as HTMLElement).closest('a')

  if (!anchor || typeof window === 'undefined') {
    return
  }

  event.preventDefault()
  event.stopPropagation()

  const url = new URL(anchor.href, window.location.origin)
  const matchedPage = url.pathname.match(/\/page\/(\d+)(?:\/)?$/)
  const targetPage = matchedPage ? Number(matchedPage[1]) : 1

  await selectPage(targetPage)
}

/** 资产储备地址支持快捷复制，并在短时间内展示成功态。 */
const copyAddress = async (address: string, rowId: string) => {
  if (!address) {
    return
  }

  if (typeof navigator !== 'undefined' && navigator.clipboard) {
    await navigator.clipboard.writeText(address)
    ElMessage({
      message: t('82423'),
      type: 'success',
      customClass: 'official-site-copy-message',
      duration: 3000,
      offset: 110,
    })
  }

  copiedRowId.value = rowId

  if (copiedTimer) {
    window.clearTimeout(copiedTimer)
  }

  copiedTimer = window.setTimeout(() => {
    copiedRowId.value = ''
    copiedTimer = null
  }, 1200)
}

onBeforeUnmount(() => {
  if (loadingTimer) {
    window.clearTimeout(loadingTimer)
  }

  if (copiedTimer) {
    window.clearTimeout(copiedTimer)
  }
})
</script>

<template>
  <main class="funds-detail-page">
    <section class="funds-detail-hero">
      <div class="funds-detail-container">
        <CommonBreadcrumb :items="breadcrumbItems" />
      </div>
    </section>

    <section class="funds-detail-container funds-detail-content">
      <div v-if="pending" class="funds-loading" aria-live="polite"></div>
      <div class="funds-card-scroll">
        <ExchangeDetailFundsCard
          class="funds-detail-card"
          :asset-strength="exchangeFunds"
          :exchange-icon="exchangeLogo"
          :exchange-name="fundsExchangeName"
          :selected-flow-time-range-type="selectedFundFlowTimeRangeType"
          :selected-volume-time-range-type="selectedTradingVolumeTimeRangeType"
          @flow-period-change="handleFundFlowPeriodChange"
          @volume-period-change="handleTradingVolumePeriodChange"
        />
      </div>

      <section ref="detailsSectionRef" class="funds-details-panel" :aria-label="t('83929')">
        <div class="funds-detail-tabs" role="tablist" :aria-label="t('83930')">
          <button
            v-for="tab in detailTabs"
            :key="tab.key"
            class="funds-detail-tab"
            :class="{ active: activeDetailTab === tab.key }"
            type="button"
            role="tab"
            :aria-selected="activeDetailTab === tab.key"
            @click="selectDetailTab(tab.key)"
          >
            {{ tab.label }}
          </button>
        </div>

        <div
          class="funds-details-table-wrap"
          :class="{
            loading: isActiveDetailLoading,
          }"
        >
          <div v-if="isActiveDetailLoading" class="funds-details-loading" aria-live="polite">
            <span class="loading-icon" aria-hidden="true"></span>
          </div>

          <!-- 资产储备 -->
          <template v-if="activeDetailTab === 'reserve'">
            <table v-if="(assetReserveListData?.data ?? []).length" class="funds-details-table">
              <thead>
                <tr>
                  <th>{{ t('82661') }}</th>
                  <th>{{ t('82724') }}</th>
                  <th>{{ t('82725') }}</th>
                  <th>{{ t('82726') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, index) in assetReserveListData?.data ?? []"
                  :key="`${row.address || row.code || row.name || 'asset'}-${index}`"
                >
                  <td>
                    <div class="coin-cell">
                      <span
                        class="coin-avatar reserve-coin-avatar"
                        :class="{ 'has-address': !!row.address }"
                        aria-hidden="true"
                      >
                        <CommonImage class="network-icon" :src="row.networkIcon" alt="" loading="eager" />
                        <CommonImage class="coin-icon" :src="row.icon" alt="" loading="eager" />
                        <span v-if="row.address" class="address-hover-tip">{{
                          t('83927', [row.networkName || '--'])
                        }}</span>
                      </span>
                      <span class="coin-meta">
                        <strong>{{ row.code || row.name }}</strong>
                        <span class="coin-address">
                          {{ row.maskedAddress }}
                          <button
                            v-if="row.address"
                            class="copy-button"
                            type="button"
                            @click="
                              copyAddress(row.address, `${row.address || row.code || row.name || 'asset'}-${index}`)
                            "
                          >
                            <span class="copy-icon" aria-hidden="true"></span>
                          </button>
                        </span>
                      </span>
                    </div>
                  </td>
                  <td>{{ row.reserveAmount || '--' }}</td>
                  <td>{{ row.price || '--' }}</td>
                  <td>{{ row.typeAmount || '--' }}</td>
                </tr>
              </tbody>
            </table>
            <CommonEmptyState v-else-if="!isActiveDetailLoading" />

            <nav
              v-if="shouldShowPagination"
              class="funds-pagination"
              :aria-label="`${t('82721')} ${t('83935')}`"
              @click.capture="handlePaginationClick"
            >
              <CommonPagination
                :current-page="currentPage"
                :total="activeDetailTotal"
                :page-size="activePageSize"
                :page-route="fundsPageRoute"
              />
            </nav>
          </template>

          <!-- 资金流向 -->
          <template v-else-if="activeDetailTab === 'flow'">
            <table v-if="(fundFlowListData?.data ?? []).length" class="funds-details-table">
              <thead>
                <tr>
                  <th>{{ t('82661') }}</th>
                  <th>{{ t('82742') }}</th>
                  <th>{{ t('82659') }}</th>
                  <th>{{ t('82743') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, index) in fundFlowListData?.data ?? []"
                  :key="`${row.name || row.wallet || 'flow'}-${index}`"
                >
                  <td>
                    <div class="coin-cell">
                      <CommonImage
                        class="size-[32px] shrink-0 object-contain"
                        :src="row.logo"
                        alt=""
                        loading="eager"
                        object-fit="contain"
                      />
                      <span class="coin-meta">
                        <strong>{{ row.name || '--' }}</strong>
                      </span>
                    </div>
                  </td>
                  <td>{{ row.wallet || '--' }}</td>
                  <td>
                    <span
                      class="flow-value"
                      :class="Number(row.volume24h ?? 0) >= 0 ? 'flow-value-positive' : 'flow-value-negative'"
                    >
                      {{ row.volume24hValue || '--' }}
                    </span>
                  </td>
                  <td>{{ row.position || '--' }}</td>
                </tr>
              </tbody>
            </table>
            <CommonEmptyState v-else-if="!isActiveDetailLoading" />

            <nav
              v-if="shouldShowPagination"
              class="funds-pagination"
              :aria-label="`${t('82722')} ${t('83935')}`"
              @click.capture="handlePaginationClick"
            >
              <CommonPagination
                :current-page="currentPage"
                :total="activeDetailTotal"
                :page-size="activePageSize"
                :page-route="fundsPageRoute"
              />
            </nav>
          </template>

          <!-- 交易量 -->
          <template v-else-if="activeDetailTab === 'volume'">
            <div class="funds-subtabs-wrap">
              <div class="funds-subtabs" role="tablist" :aria-label="t('83931')">
                <button
                  v-for="tab in tradingVolumeCategoryTabs"
                  :key="tab.key"
                  class="funds-subtab"
                  :class="{ active: activeVolumeCategory === tab.key }"
                  type="button"
                  role="tab"
                  :aria-selected="activeVolumeCategory === tab.key"
                  @click="selectVolumeCategory(tab.key)"
                >
                  {{ tab.label }}
                </button>
              </div>
            </div>

            <table v-if="(tradingVolumeListData?.data ?? []).length" class="funds-details-table">
              <thead>
                <tr>
                  <th>{{ t('82661') }}</th>
                  <th>{{ t('82662') }}</th>
                  <th v-if="activeVolumeTableVariant === 'spot'">{{ t('82506') }}</th>
                  <th v-if="activeVolumeTableVariant === 'spot'">{{ t('82736') }}</th>
                  <th v-if="activeVolumeTableVariant === 'perpetual'">{{ t('82737') }}</th>
                  <th v-if="activeVolumeTableVariant === 'perpetual'">{{ t('82506') }}</th>
                  <th v-if="activeVolumeTableVariant === 'futures'">{{ t('82737') }}</th>
                  <th v-if="activeVolumeTableVariant === 'futures'">{{ t('82738') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, index) in tradingVolumeListData?.data ?? []"
                  :key="`${row.pairName || row.logoTopLeft || 'volume'}-${index}`"
                >
                  <td>
                    <div class="coin-cell">
                      <CommonImage
                        class="size-[32px] shrink-0 object-contain"
                        :src="row.logoTopLeft"
                        alt=""
                        loading="eager"
                        object-fit="contain"
                      />
                      <span class="coin-meta">
                        <strong>{{ row.pairName || '--' }}</strong>
                      </span>
                    </div>
                  </td>
                  <td>{{ row.pairName || '--' }}</td>
                  <td v-if="activeVolumeTableVariant === 'spot'">{{ row.volume24h || '--' }}</td>
                  <td v-if="activeVolumeTableVariant === 'spot'">
                    <div class="ratio-cell">
                      <span
                        class="ratio-track"
                        :class="{ 'has-gap': row.ratio && row.ratio !== '100%' }"
                        :style="{ '--ratio-width': row.ratio || '0%' }"
                        aria-hidden="true"
                      >
                        <span
                          class="ratio-fill"
                          :class="{ 'ratio-fill-slanted': row.ratio && row.ratio !== '100%' }"
                          :style="{ width: row.ratio || '0%' }"
                        ></span>
                      </span>
                      <span class="ratio-text">{{ row.ratio || '--' }}</span>
                    </div>
                  </td>
                  <td v-if="activeVolumeTableVariant === 'perpetual'">{{ row.openInterest || '--' }}</td>
                  <td v-if="activeVolumeTableVariant === 'perpetual'">{{ row.volume24h || '--' }}</td>
                  <td v-if="activeVolumeTableVariant === 'futures'">{{ row.openInterest || '--' }}</td>
                  <td v-if="activeVolumeTableVariant === 'futures'">{{ row.expiryTime || '--' }}</td>
                </tr>
              </tbody>
            </table>
            <CommonEmptyState v-else-if="!isActiveDetailLoading" />

            <nav
              v-if="shouldShowPagination"
              class="funds-pagination"
              :aria-label="`${t('82723')} ${t('83935')}`"
              @click.capture="handlePaginationClick"
            >
              <CommonPagination
                :current-page="currentPage"
                :total="activeDetailTotal"
                :page-size="activePageSize"
                :page-route="fundsPageRoute"
              />
            </nav>
          </template>
        </div>
      </section>
    </section>
  </main>
</template>

<style scoped lang="less">
.funds-detail-page {
  min-height: 100vh;
  color: #292a2e;
}

.funds-detail-hero {
  border-radius: 0 0 4px 4px;
  background: #ffffff;
  color: #292a2e;
  padding-top: 70px;
}

.funds-detail-container {
  width: min(1200px, calc(100vw - 48px));
  margin: 0 auto;
}

.funds-detail-content {
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding-bottom: 80px;
}

.funds-card-scroll {
  width: 100%;
  border-radius: 12px;
}

.funds-detail-card {
  width: 100%;
}

.funds-loading {
  width: 100%;
  border: 1px solid rgba(11, 18, 40, 0.08);
  border-radius: 8px;
  background: #ffffff;
  padding: 14px 16px;
  color: #7d818a;
  font-size: 14px;
  line-height: 22px;
}

.funds-details-panel {
  width: 100%;
  border-radius: 12px;
  background: #ffffff;
  color: #292a2e;
}

.funds-detail-tabs {
  display: flex;
  align-items: center;
  gap: 24px;
  border-bottom: 0.5px solid rgba(5, 21, 36, 0.06);
}

.funds-detail-tab {
  position: relative;
  height: 44px;
  padding: 0 0 16px;
  color: #8c8f97;
  background: transparent;
  font-size: 20px;
  font-weight: 400;
  line-height: 28px;
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.18s ease;
}

.funds-detail-tab::after {
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 28px;
  height: 4px;
  border-radius: 999px;
  background: #292a2e;
  content: '';
  opacity: 0;
  transform: translateX(-50%);
  transition: opacity 0.18s ease;
}

.funds-detail-tab:hover,
.funds-detail-tab.active {
  color: #292a2e;
}

.funds-detail-tab.active {
  font-weight: 700;
}

.funds-detail-tab.active::after {
  opacity: 1;
}

.funds-details-table-wrap {
  position: relative;
  min-height: 420px;
}

.funds-details-table-wrap.loading {
  pointer-events: none;
}

.funds-details-table-wrap.loading .funds-details-table,
.funds-details-table-wrap.loading .funds-pagination,
.funds-details-table-wrap.loading .funds-details-empty {
  opacity: 0.36;
}

.funds-details-loading {
  position: absolute;
  inset: 0;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.54);
  color: #505258;
  font-size: 14px;
  line-height: 22px;
}

.loading-icon {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(41, 42, 46, 0.18);
  border-top-color: #292a2e;
  border-radius: 50%;
  animation: detailLoadingSpin 0.8s linear infinite;
}

.funds-details-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.funds-details-table th,
.funds-details-table td {
  border-bottom: 0.5px solid rgba(5, 21, 36, 0.06);
  vertical-align: middle;
}

.funds-details-table th {
  padding: 16px 12px 12px;
  color: #7d818a;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  text-align: right;
  white-space: nowrap;
}

.funds-details-table th:first-child {
  padding-left: 0;
  text-align: left;
}

.funds-details-table th:last-child {
  padding-right: 0;
}

.funds-details-table td {
  padding: 16px 12px;
  color: #292a2e;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  text-align: right;
  white-space: nowrap;
  transition: background-color 0.18s ease;
}

.funds-details-table td:first-child {
  padding-left: 0;
  text-align: left;
}

.funds-details-table td:last-child {
  padding-right: 0;
}

.funds-details-table tbody tr:hover td {
  background: #f8f8f8;
}

.coin-cell {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  cursor: pointer;
}

.coin-avatar {
  position: relative;
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
}

.reserve-coin-avatar.has-address {
  cursor: pointer;
}

.coin-avatar .network-icon {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 20px;
  height: 20px;
}

.coin-avatar .network-icon :deep(img) {
  position: absolute;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  object-fit: cover;
}

.coin-avatar .coin-icon {
  position: absolute;
  top: 0;
  left: 0;
  width: 22px;
  height: 22px;
}

.coin-avatar .coin-icon :deep(img) {
  position: absolute;
  width: 22px;
  height: 22px;
  border: 1px solid #ffffff;
  border-radius: 50%;
  object-fit: cover;
}

.address-hover-tip {
  position: absolute;
  top: calc(100% + 10px);
  left: 50%;
  z-index: 3;
  border-radius: 4px;
  background: #ffffff;
  padding: 4px 8px;
  color: #505258;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transform: translateX(-50%) translateY(-4px);
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
  box-shadow:
    0 0 0.5px rgba(0, 0, 0, 0.2),
    0 8px 10px rgba(0, 0, 0, 0.12);
}

.address-hover-tip::after {
  position: absolute;
  bottom: 100%;
  left: 50%;
  border-width: 5px;
  border-style: solid;
  border-color: transparent transparent #ffffff transparent;
  content: '';
  transform: translateX(-50%);
}

.reserve-coin-avatar.has-address:hover .address-hover-tip {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

.coin-avatar-single {
  // display: inline-flex;
  // align-items: center;
  // justify-content: center;
}

.coin-icon-single {
  position: static !important;
  width: 32px !important;
  height: 32px !important;
}

.coin-icon-single :deep(img) {
  // position: static !important;
  width: 32px !important;
  height: 32px !important;
}

.coin-meta {
  display: flex;
  min-width: 0;
  flex-direction: column;
  justify-content: center;
}

.coin-meta strong {
  overflow: hidden;
  color: #292a2e;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.coin-address {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #505258;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
}

.copy-button {
  position: relative;
  display: inline-flex;
  width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
  border-radius: 2px;
  color: #7d818a;
  cursor: pointer;
  opacity: 0;
  transition:
    background-color 0.18s ease,
    color 0.18s ease,
    opacity 0.18s ease;
}

.funds-details-table tbody tr:hover .copy-button,
.copy-button:focus-visible {
  opacity: 1;
}

.copy-icon,
.copy-icon::after {
  display: block;
  width: 9px;
  height: 10px;
  border: 1.4px solid currentColor;
  border-radius: 2px;
  content: '';
}

.copy-icon {
  position: relative;
  transform: translate(1px, -1px);
}

.copy-icon::after {
  position: absolute;
  top: 3px;
  left: -4px;
  background: #ffffff;
}

.copy-tip {
  position: absolute;
  top: 22px;
  left: 0;
  z-index: 2;
  border-radius: 4px;
  background: #ffffff;
  padding: 4px 8px;
  color: #505258;
  font-size: 13px;
  line-height: 20px;
  white-space: nowrap;
  box-shadow:
    0 0 0.5px rgba(0, 0, 0, 0.2),
    0 8px 10px rgba(0, 0, 0, 0.12);
}

.flow-value {
  font-weight: 600;
}

.flow-value-positive {
  color: #14925a;
}

.flow-value-negative {
  color: #d94b40;
}

.funds-subtabs-wrap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 0 16px;
}

.funds-subtabs {
  display: flex;
  align-items: center;
  gap: 8px;
}

.funds-subtab {
  display: inline-flex;
  height: 32px;
  align-items: center;
  justify-content: center;
  border: 0.5px solid rgba(11, 18, 40, 0.14);
  border-radius: 2px;
  background: #fff;
  padding: 0 12px;
  color: #292a2e;
  font-size: 13px;
  line-height: 20px;
  cursor: pointer;
}

.funds-subtab.active {
  border-color: #0a1c54;
  background: #0a1c54;
  color: #fff;
}

.ratio-cell {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 0 16px 12px;
}

.ratio-track {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  width: 120px;
  height: 6px;
  overflow: hidden;
  border-radius: 999px;
  background: #f3f4f6;
}

.ratio-track::after {
  position: absolute;
  top: 50%;
  left: var(--ratio-width, 0%);
  display: none;
  width: 6px;
  height: 100%;
  background: #fff;
  content: '';
  pointer-events: none;
  transform: translate(0, -50%) skewX(-35deg);
  transform-origin: left center;
}

.ratio-track.has-gap::after {
  display: block;
}

.ratio-fill {
  position: relative;
  display: inline-flex;
  height: 100%;
  background: #1868db;
  border-radius: 999px;
}

.ratio-fill-slanted::after {
  position: absolute;
  top: 0;
  right: -3px;
  width: 6px;
  height: 100%;
  background: #1868db;
  content: '';
  transform: skewX(-35deg);
  transform-origin: right center;
}

.ratio-text {
  min-width: 44px;
  color: #292a2e;
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  text-align: right;
}

.funds-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 20px;
  flex-wrap: wrap;
}

[dir='rtl'] .funds-detail-page .funds-details-table th:first-child,
[dir='rtl'] .funds-detail-page .funds-details-table td:first-child {
  padding-right: 0;
  padding-left: 12px;
  text-align: right;
}

[dir='rtl'] .funds-detail-page .funds-details-table th:last-child,
[dir='rtl'] .funds-detail-page .funds-details-table td:last-child {
  padding-right: 12px;
  padding-left: 0;
}

[dir='rtl'] .funds-detail-page .coin-avatar .network-icon {
  right: auto;
  left: 0;
}

[dir='rtl'] .funds-detail-page .coin-avatar .coin-icon {
  right: 0;
  left: auto;
}

[dir='rtl'] .funds-detail-page .ratio-cell {
  justify-content: flex-start;
  padding-right: 12px;
  padding-left: 0;
}

[dir='rtl'] .funds-detail-page .ratio-track::after {
  right: var(--ratio-width, 0%);
  left: auto;
  transform: translate(0, -50%) skewX(35deg);
  transform-origin: right center;
}

[dir='rtl'] .funds-detail-page .ratio-fill-slanted::after {
  right: auto;
  left: -3px;
  transform: skewX(35deg);
  transform-origin: left center;
}

@keyframes detailLoadingSpin {
  to {
    transform: rotate(360deg);
  }
}

@media screen and (max-width: 799px) {
  .funds-detail-container {
    width: min(1200px, calc(100vw - 32px));
  }
}
</style>

<style lang="less">
/* Figma copy-success toast, scoped to the fund-security page bundle. */
.official-site-copy-message.el-message {
  min-width: 0;
  min-height: 48px;
  padding: 12px 16px;
  gap: 8px;
  border: 0.5px solid rgba(11, 18, 40, 0.14);
  border-radius: 4px;
  background: #ffffff;
  box-shadow: none;
}

.official-site-copy-message.el-message .el-message__icon {
  width: 20px;
  height: 20px;
  margin: 0;
  color: #22a06b;
}

.official-site-copy-message.el-message .el-message__content {
  color: #292a2e;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  white-space: nowrap;
}
</style>
